import type { HingeSide } from '../drawing/door';

/**
 * Hinge and handle drilling for one made-to-measure piece.
 *
 * <p>Ported from the site mockup (`doors.html`), where every rule below was
 * checked against real renders. Pure functions over plain data, so the builder,
 * the server-side re-check (plan F1) and the tests share one model.
 *
 * <h3>Coordinates</h3>
 * <ul>
 *   <li>Hinge positions are cup centres in mm ALONG the hinged edge: from the
 *       top for a left/right-hung door, from the left (seen from the front) for
 *       a top/bottom-hung flap.</li>
 *   <li>The handle centre is (x, y) in mm from the top-left corner SEEN FROM
 *       THE FRONT. The back view mirrors it.</li>
 * </ul>
 */

export const CUP_D = 35;
export const SCREW_D = 8;
/** The standard 45/9.5 screw pattern of a 35 mm cup hinge. */
export const SCREW_PITCH = 45;
export const SCREW_OFFSET = 9.5;
export const EDGE_STD = 22.5;
export const EDGE_MIN = 20.5;
export const EDGE_MAX = 24.5;
export const END_MIN = 60;
export const GAP_MIN = 80;
export const MAX_HINGES = 6;

export const HANDLE_CENTRES = [64, 96, 128, 160, 192, 224, 256, 320] as const;
export const HOLE_D = 5;
export const HOLE_EDGE_MIN = 12;
export const HANDLE_INSET = 35;

/** Size limits of a made-to-measure piece, in mm. */
export const SIZE_LIMITS = { minH: 110, maxH: 2400, minW: 110, maxW: 1200 } as const;

export type PieceKind = 'door' | 'drawer' | 'panel';

export const PIECE_LABEL: Record<PieceKind, string> = { door: 'Door', drawer: 'Drawer front', panel: 'End panel' };

export interface HingeDrilling {
  side: HingeSide;
  count: number;
  positions: number[];
  /** Cup centre from the hinged edge, mm. */
  edge: number;
  /** The shopper set their own count or positions; a size change will not move them. */
  custom: boolean;
}

export type HandleDrillType = 'none' | 'single' | 'double';
export type HandleOrientation = 'vertical' | 'horizontal';

export interface HandleDrilling {
  type: HandleDrillType;
  /** Hole centres of a two-hole handle, mm. */
  centres: number;
  orient: HandleOrientation;
  x: number;
  y: number;
  custom: boolean;
}

export interface Piece {
  kind: PieceKind;
  h: number;
  w: number;
  qty: number;
  hinge: HingeDrilling;
  handle: HandleDrilling;
}

/** The handle product chosen for the whole order; it decides the default drilling. */
export type HandleProduct = 'bar' | 'knob' | 'cup' | 'none';

const DRILL_FOR: Record<HandleProduct, HandleDrillType> = { bar: 'double', knob: 'single', cup: 'double', none: 'none' };

export function drillTypeFor(product: HandleProduct): HandleDrillType {
  return DRILL_FOR[product];
}

export function isFlap(side: HingeSide): boolean {
  return side === 'top' || side === 'bottom';
}

/** Length of the hinged edge. */
export function hingedLength(piece: Pick<Piece, 'h' | 'w'>, side: HingeSide): number {
  return isFlap(side) ? piece.w : piece.h;
}

/** Standard hinge count for a hinged edge of `length` mm. */
export function standardCount(length: number): number {
  return length <= 900 ? 2 : length <= 1500 ? 3 : length <= 2000 ? 4 : 5;
}

/** Standard cup positions: 100 mm from each end (70 mm on a short edge), the rest evenly between. */
export function standardPositions(length: number, count: number): number[] {
  if (count <= 0) return [];
  const end = length < 300 ? 70 : 100;
  if (count === 1) return [Math.round(length / 2)];
  return Array.from({ length: count }, (_, i) => Math.round(end + ((length - 2 * end) / (count - 1)) * i));
}

export function standardHinge(side: HingeSide, length: number): HingeDrilling {
  if (side === 'none') return { side, count: 0, positions: [], edge: EDGE_STD, custom: false };
  const count = standardCount(length);
  return { side, count, positions: standardPositions(length, count), edge: EDGE_STD, custom: false };
}

/** Re-derives standard positions after a size change, unless the shopper set their own. */
export function refitHinge(piece: Piece): HingeDrilling {
  const hinge = piece.hinge;
  if (hinge.side === 'none' || hinge.custom) return hinge;
  const length = hingedLength(piece, hinge.side);
  const count = standardCount(length);
  return { ...hinge, count, positions: standardPositions(length, count) };
}

/** Plain-English problems with the hinge drilling; empty when it fits. */
export function hingeIssues(piece: Piece): string[] {
  const hinge = piece.hinge;
  if (hinge.side === 'none') return [];
  const length = hingedLength(piece, hinge.side);
  const out: string[] = [];
  const sorted = [...hinge.positions].sort((a, b) => a - b);
  sorted.forEach((p, i) => {
    if (p < END_MIN) out.push(`Hinge ${i + 1} is ${p}mm from the end. Keep at least ${END_MIN}mm.`);
    if (p > length - END_MIN) out.push(`Hinge ${i + 1} is ${length - p}mm from the far end. Keep at least ${END_MIN}mm.`);
    const previous = sorted[i - 1];
    if (previous !== undefined && p - previous < GAP_MIN) {
      out.push(`Hinges ${i} and ${i + 1} are only ${p - previous}mm apart. Keep at least ${GAP_MIN}mm.`);
    }
  });
  if (hinge.edge < EDGE_MIN || hinge.edge > EDGE_MAX) {
    out.push(`A cup centre ${hinge.edge}mm from the edge is outside ${EDGE_MIN}–${EDGE_MAX}mm for standard hinges.`);
  }
  if (!isFlap(hinge.side) && piece.w < 2 * hinge.edge + CUP_D) out.push('The door is too narrow for a hinge cup.');
  if (isFlap(hinge.side) && piece.h < 2 * hinge.edge + CUP_D) out.push('The flap is too shallow for a hinge cup.');
  return out;
}

/**
 * The standard handle position: on the opening edge near the top for a door,
 * centred on a drawer front or a flap. An end panel gets no handle.
 */
export function standardHandle(
  piece: Pick<Piece, 'kind' | 'h' | 'w' | 'hinge'>,
  product: HandleProduct,
  keep?: { type?: HandleDrillType; centres?: number },
): HandleDrilling {
  const type = keep?.type ?? (piece.kind === 'panel' ? 'none' : drillTypeFor(product));
  const centres = keep?.centres ?? (product === 'cup' ? 64 : 128);
  const side = piece.hinge.side;
  let x: number;
  let y: number;
  let orient: HandleOrientation;
  if (piece.kind === 'drawer') {
    x = piece.w / 2;
    y = piece.h / 2;
    orient = 'horizontal';
  } else if (isFlap(side)) {
    x = piece.w / 2;
    y = side === 'top' ? piece.h - HANDLE_INSET : HANDLE_INSET;
    orient = 'horizontal';
  } else {
    x = side === 'right' ? HANDLE_INSET : piece.w - HANDLE_INSET;
    orient = 'vertical';
    y = piece.h > 1200 ? piece.h / 2 : Math.min(piece.h / 2, (type === 'double' ? centres / 2 : 0) + 60);
  }
  return { type, centres, orient, x: Math.round(x), y: Math.round(y), custom: false };
}

export function refitHandle(piece: Piece, product: HandleProduct): HandleDrilling {
  if (piece.handle.custom) return piece.handle;
  return standardHandle(piece, product, { type: piece.handle.type, centres: piece.handle.centres });
}

/** The handle holes, front coordinates. */
export function handleHoles(handle: HandleDrilling): Array<[number, number]> {
  if (handle.type === 'none') return [];
  if (handle.type === 'single') return [[handle.x, handle.y]];
  const c = handle.centres / 2;
  return handle.orient === 'vertical'
    ? [[handle.x, handle.y - c], [handle.x, handle.y + c]]
    : [[handle.x - c, handle.y], [handle.x + c, handle.y]];
}

/** Hinge cup centres, front coordinates, for clash checks. */
export function cupCentres(piece: Piece): Array<[number, number]> {
  const { side, edge, positions } = piece.hinge;
  return positions.map((p): [number, number] => {
    switch (side) {
      case 'left':
        return [edge, p];
      case 'right':
        return [piece.w - edge, p];
      case 'top':
        return [p, edge];
      case 'bottom':
        return [p, piece.h - edge];
      default:
        return [Number.NaN, Number.NaN];
    }
  }).filter(([x]) => Number.isFinite(x));
}

export function handleIssues(piece: Piece): string[] {
  const out: string[] = [];
  const cups = cupCentres(piece);
  handleHoles(piece.handle).forEach(([x, y], i) => {
    const margin = Math.min(x, y, piece.w - x, piece.h - y);
    if (margin < HOLE_EDGE_MIN) out.push(`Handle hole ${i + 1} is ${Math.round(margin)}mm from an edge. Keep at least ${HOLE_EDGE_MIN}mm.`);
    if (cups.some(([cx, cy]) => Math.hypot(x - cx, y - cy) < CUP_D / 2 + 15)) out.push(`Handle hole ${i + 1} is too close to a hinge cup.`);
  });
  return out;
}

export function sizeIssues(piece: Pick<Piece, 'h' | 'w' | 'qty'>): string[] {
  const out: string[] = [];
  const { minH, maxH, minW, maxW } = SIZE_LIMITS;
  if (!(piece.h >= minH && piece.h <= maxH)) out.push(`Height must be ${minH}–${maxH}mm.`);
  if (!(piece.w >= minW && piece.w <= maxW)) out.push(`Width must be ${minW}–${maxW}mm.`);
  if (!(Number.isInteger(piece.qty) && piece.qty >= 1)) out.push('Quantity must be at least 1.');
  return out;
}

/** A new piece with standard drilling. */
export function newPiece(kind: PieceKind, h: number, w: number, side: HingeSide, qty: number, product: HandleProduct): Piece {
  const hingeSide = kind === 'door' ? side : 'none';
  const hinge = standardHinge(hingeSide, hingedLength({ h, w }, hingeSide));
  const base = { kind, h, w, qty, hinge };
  return { ...base, handle: standardHandle(base, product) };
}
